import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ArrowLeft, User, Building, Save } from 'lucide-react';

interface DoctorProfile {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
}

export function DoctorSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingClinic, setSavingClinic] = useState(false);
  
  const [profile, setProfile] = useState<DoctorProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
  });
  
  const [clinicForm, setClinicForm] = useState({
    clinic_name: '',
    reminder_enabled: true,
    reminder_days_before: '7,3,1',
    reminder_days_after: '1,3',
  });
  const [clinicIdState, setClinicIdState] = useState<number | null>(null);

  useEffect(() => {
    // Small delay to ensure ProtectedRoute has finished token setup
    const timer = setTimeout(() => {
      loadData();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const loadData = async () => {
    try {
      // Verify we have a token before proceeding
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.error('[Settings] No auth token found in localStorage');
        toast({
          title: t('app.error'),
          description: 'Authentication token missing. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      
      console.log('[Settings] Token verified, loading data...');
      
      // Load profile and settings in parallel
      const [profileResult, settingsResult]: any = await Promise.all([
        api.getDoctorProfile().catch((e) => {
          console.error('[Settings] getDoctorProfile failed:');
          console.error('[Settings] Error object:', e);
          console.error('[Settings] Error message:', e?.message);
          console.error('[Settings] Error name:', e?.name);
          return null;
        }),
        api.getClinicSettings().catch((e) => {
          console.error('[Settings] getClinicSettings failed:');
          console.error('[Settings] Error object:', e);
          console.error('[Settings] Error message:', e?.message);
          return null;
        }),
      ]);

      console.log('[Settings] Profile result:', profileResult);
      console.log('[Settings] Profile result type:', typeof profileResult);
      console.log('[Settings] Settings result:', settingsResult);

      // Normalize profile shape (supports { profile: {...} } or direct {...})
      const profileData = (profileResult && typeof profileResult === 'object')
        ? (profileResult.profile ?? profileResult)
        : null;

      if (!profileData || !profileData.email) {
        console.error('[Settings] Profile data is missing or invalid:', profileResult);
        console.error('[Settings] This indicates the API call failed - check network tab for details');
        // Keep last known profile (avoid wiping UI on transient failures)
      } else {
        setProfile(profileData);
        setProfileForm({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
        });
      }

      // Normalize settings response to handle both { clinic: {...} } or direct {...}
      const clinicData = (settingsResult && typeof settingsResult === 'object')
        ? (settingsResult.clinic ?? settingsResult)
        : null;

      console.log('[Settings] Clinic data:', clinicData);

      // If we have clinic data, populate the form
      if (clinicData && clinicData.id) {
        setClinicIdState(clinicData.id);
        setClinicForm({
          clinic_name: clinicData.name || '',
          reminder_enabled: clinicData.reminder_enabled ?? true,
          reminder_days_before: clinicData.reminder_days_before || '7,3,1',
          reminder_days_after: clinicData.reminder_days_after || '1,3',
        });
      }
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      console.error('[Settings] Load error:', error);
      toast({
        title: t('app.error'),
        description: error?.message || t('app.error'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);

    try {
      const result: any = await api.updateDoctorProfile({
        first_name: profileForm.first_name || undefined,
        last_name: profileForm.last_name || undefined,
      });

      setProfile(result.profile);
      toast({
        title: t('app.success'),
        description: t('profile.profileUpdated'),
      });
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveClinic = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingClinic(true);

    try {
      // Persist clinic name to the clinics table via clinicId
      if (clinicIdState) {
        await api.updateClinic(clinicIdState, { name: clinicForm.clinic_name });
      }

      // Persist reminder settings to doctor settings endpoint
      await api.updateClinicSettings({
        reminder_enabled: clinicForm.reminder_enabled,
        reminder_days_before: clinicForm.reminder_days_before,
        reminder_days_after: clinicForm.reminder_days_after,
      });

      toast({
        title: t('app.success'),
        description: t('clinic.settingsUpdated'),
      });
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setSavingClinic(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">⚙️ {t('nav.settings')}</h1>
          </div>
          <LanguageToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Doctor Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('profile.profile')}
            </CardTitle>
            <CardDescription>
              {profile?.email}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">{t('profile.firstName')}</Label>
                  <Input
                    id="first_name"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">{t('profile.lastName')}</Label>
                  <Input
                    id="last_name"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={savingProfile}>
                  <Save className="h-4 w-4 mr-2" />
                  {savingProfile ? t('app.loading') : t('common.save')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Clinic Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              {t('clinic.clinicSettings')}
            </CardTitle>
            <CardDescription>
              {t('clinic.clinicSettingsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveClinic} className="space-y-4">
              <div>
                <Label htmlFor="clinic_name">{t('clinic.clinicName')}</Label>
                <Input
                  id="clinic_name"
                  value={clinicForm.clinic_name}
                  onChange={(e) => setClinicForm({ ...clinicForm, clinic_name: e.target.value })}
                  placeholder="My Pediatric Clinic"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="reminder_enabled"
                  checked={clinicForm.reminder_enabled}
                  onChange={(e) => setClinicForm({ ...clinicForm, reminder_enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="reminder_enabled">{t('clinic.enableEmailReminders')}</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reminder_days_before">{t('clinic.daysBeforeAppointment')}</Label>
                  <Input
                    id="reminder_days_before"
                    value={clinicForm.reminder_days_before}
                    onChange={(e) => setClinicForm({ ...clinicForm, reminder_days_before: e.target.value })}
                    placeholder="7,3,1"
                  />
                  <p className="text-sm text-gray-500 mt-1">{t('clinic.commaSeparatedDays')}</p>
                </div>
                <div>
                  <Label htmlFor="reminder_days_after">{t('clinic.daysAfterMissed')}</Label>
                  <Input
                    id="reminder_days_after"
                    value={clinicForm.reminder_days_after}
                    onChange={(e) => setClinicForm({ ...clinicForm, reminder_days_after: e.target.value })}
                    placeholder="1,3"
                  />
                  <p className="text-sm text-gray-500 mt-1">{t('clinic.commaSeparatedDays')}</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={savingClinic}>
                  <Save className="h-4 w-4 mr-2" />
                  {savingClinic ? t('app.loading') : t('common.save')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
